import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { refreshVods, useChannelsMeta, useVods } from '../hooks/useKick'
import { formatAbsolute, formatDuration, formatTimecode } from '../lib/format'
import { useAppState } from '../state/appState'
import type { Vod } from '../types'
import { Avatar } from './Avatar'
import { timelineHitPadding } from '../lib/timelineTargets'
import { useChatActivity } from '../hooks/useChatActivity'
import { ChatActivityStatus } from './ChatActivityStatus'
import { ChatActivityGraph } from './ChatActivityGraph'
import type { ActivityInspectionSource, ActivityInspectionState } from '../lib/activityInspection'

const HOUR_MS = 60 * 60 * 1000
const RANGES = [
  { hours: 24, label: '24h', description: 'Last 24 hours' },
  { hours: 72, label: '3 days', description: 'Last 3 days' },
  { hours: 168, label: '7 days', description: 'Last 7 days' },
] as const
const CHANNEL_COLORS = ['#f87171', '#4ade80', '#fb923c', '#a78bfa', '#facc15', '#2dd4bf']

function channelColor(slug: string): string {
  if (slug === 'destiny') return '#38bdf8'
  if (slug === 'anythingelse') return '#f472b6'
  // Stable across channel additions, removals, and changes in ordering.
  const hash = Array.from(slug).reduce((value, char) => ((value * 31) + char.charCodeAt(0)) >>> 0, 0)
  return CHANNEL_COLORS[hash % CHANNEL_COLORS.length]
}

const timeFormat = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
})
const dateFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
})
const dayFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric' })

function recordingEnd(vod: Vod, windowEnd: number): number {
  // Kick can report zero duration while the recording is still growing.
  return vod.isLive ? windowEnd : vod.startTime + vod.durationMs
}

function playbackOffset(vod: Vod, timestamp: number, windowEnd: number): number {
  // Stay inside the recording, including when clicking its right-hand edge.
  return Math.floor(Math.max(0, Math.min(recordingEnd(vod, windowEnd) - vod.startTime - 1, timestamp - vod.startTime)) / 1000)
}

function playerUrl(vod: Vod, seconds = 0): string {
  return `/v/${encodeURIComponent(vod.channelSlug)}/${vod.id}?t=${formatTimecode(seconds)}`
}

type ChannelStatus = {
  loading: boolean
  error: string | null
  empty: boolean
  retry: () => void
}

function ChannelRow({ slug, start, end, ticks, chartWidth, highlightClass, onStatus }: {
  slug: string
  start: number
  end: number
  ticks: number[]
  chartWidth: number
  highlightClass: string
  onStatus: (slug: string, status: ChannelStatus) => void
}) {
  const rangeMs = end - start
  const navigate = useNavigate()
  const rowRef = useRef<HTMLDivElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const touchPointer = useRef(false)
  const { data: vods, loading, error, reload } = useVods(slug)
  const tooltipId = useId()
  const [hoveredId, setHoveredId] = useState<number | null>(null)
  const [focusedId, setFocusedId] = useState<number | null>(null)
  const [pointerTime, setPointerTime] = useState<number | null>(null)
  const [touchSelection, setTouchSelection] = useState<{ id: number; time: number } | null>(null)

  useEffect(() => {
    if (!touchSelection) return
    const dismissOutside = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node) || popupRef.current?.contains(target)) return
      const bar = target instanceof Element ? target.closest('.timeline-vod') : null
      if (bar && rowRef.current?.contains(bar)) return
      setTouchSelection(null)
      setHoveredId(null)
      setFocusedId(null)
    }
    document.addEventListener('pointerdown', dismissOutside)
    return () => document.removeEventListener('pointerdown', dismissOutside)
  }, [touchSelection])
  const visibleVods = (vods ?? []).filter((vod) => (
    Number.isFinite(vod.startTime)
    && vod.startTime > 0
    && Number.isFinite(vod.durationMs)
    && (vod.isLive || vod.durationMs > 0)
    && vod.startTime < end
    && recordingEnd(vod, end) > start
  ))
  const hitPadding = timelineHitPadding(visibleVods.map((vod) => ({
    start: (Math.max(start, vod.startTime) - start) / rangeMs * chartWidth,
    end: (Math.min(end, recordingEnd(vod, end)) - start) / rangeMs * chartWidth,
  })), chartWidth)
  const isLoading = loading || (!vods && !error)
  const isEmpty = Boolean(vods) && visibleVods.length === 0
  useEffect(() => {
    onStatus(slug, { loading: isLoading, error, empty: isEmpty, retry: reload })
  }, [onStatus, slug, isLoading, error, isEmpty, reload])
  const activeVod = visibleVods.find((vod) => vod.id === (touchSelection?.id ?? hoveredId ?? focusedId))
  const selectedTime = touchSelection?.time ?? (hoveredId === activeVod?.id ? pointerTime : null)
  const previewTime = activeVod
    ? selectedTime !== null
      ? Math.max(activeVod.startTime, Math.min(recordingEnd(activeVod, end) - 1, selectedTime))
      : activeVod.startTime
    : null
  const tooltipTime = (touchSelection !== null || hoveredId !== null) && previewTime !== null
    ? previewTime
    : Math.max(start, activeVod?.startTime ?? start)

  const timestampAt = (clientX: number): number => {
    const rect = rowRef.current?.getBoundingClientRect()
    if (!rect?.width) return start
    return start + Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * rangeMs
  }

  return (
    <div
      ref={rowRef}
      className={`timeline-vod-row ${highlightClass}`}
      aria-label={`${slug} recordings`}
      onMouseLeave={() => setHoveredId(null)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocusedId(null)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setTouchSelection(null)
          setHoveredId(null)
          setFocusedId(null)
        }
      }}
    >
      {ticks.map((time) => (
        <div key={time} className="timeline-tick" style={{ left: `${((time - start) / rangeMs) * 100}%` }} />
      ))}
      {visibleVods.map((vod, index) => {
        const vodEnd = recordingEnd(vod, end)
        const visibleStart = Math.max(start, vod.startTime)
        const visibleEnd = Math.min(end, vodEnd)
        const details = `${slug} · ${formatAbsolute(vod.startTime)} – ${vod.isLive ? 'LIVE, ongoing' : formatAbsolute(vodEnd)} · ${vod.isLive ? 'Elapsed' : 'Duration'} ${formatDuration(vodEnd - vod.startTime)}`
        const className = `timeline-vod${vod.isLive ? ' is-live' : ''}${vod.subscriberOnly ? ' is-subscriber-only' : ''}`
        const style = {
          left: `${((visibleStart - start) / rangeMs) * 100}%`,
          width: `${((visibleEnd - visibleStart) / rangeMs) * 100}%`,
          backgroundColor: channelColor(slug),
          '--timeline-hit-before': `${-hitPadding[index].before}px`,
          '--timeline-hit-after': `${-hitPadding[index].after}px`,
        } as CSSProperties
        const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
          touchPointer.current = event.pointerType === 'touch' || event.pointerType === 'pen'
          if (!touchPointer.current) setTouchSelection(null)
        }
        const onPointerCancel = () => { touchPointer.current = false }
        const onPointerEnter = (event: ReactPointerEvent<HTMLElement>) => {
          if (event.pointerType === 'mouse') setTouchSelection(null)
        }
        const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
          if (event.key === 'Enter') touchPointer.current = false
        }
        const onMouseEnter = (event: ReactMouseEvent<HTMLElement>) => {
          setHoveredId(vod.id)
          setPointerTime(timestampAt(event.clientX))
        }
        const onMouseMove = (event: ReactMouseEvent<HTMLElement>) => {
          setPointerTime(timestampAt(event.clientX))
        }
        const onFocus = () => {
          if (!touchPointer.current) setFocusedId(vod.id)
        }
        const onClick = (event: ReactMouseEvent<HTMLElement>) => {
          if (touchPointer.current) {
            touchPointer.current = false
            event.preventDefault()
            setTouchSelection({ id: vod.id, time: timestampAt(event.clientX) })
            setHoveredId(null)
            setFocusedId(null)
            return
          }
          if (vod.subscriberOnly) return
          // Keyboard activation follows the normal link to the beginning.
          if (event.detail === 0 || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
          event.preventDefault()
          navigate(playerUrl(vod, playbackOffset(vod, timestampAt(event.clientX), end)))
        }
        const describedBy = activeVod?.id === vod.id ? tooltipId : undefined

        if (vod.subscriberOnly) {
          return (
            <div
              key={vod.id}
              className={className}
              aria-label={`${details} · Subscriber only`}
              onPointerEnter={onPointerEnter}
              onMouseEnter={onMouseEnter}
              onMouseMove={onMouseMove}
              style={style}
            >
              {vod.isLive && <span className="timeline-live-marker" aria-hidden="true" />}
            </div>
          )
        }

        return (
          <Link
            key={vod.id}
            className={className}
            to={playerUrl(vod)}
            aria-label={`Watch ${details} from start`}
            aria-describedby={describedBy}
            onPointerDown={onPointerDown}
            onPointerCancel={onPointerCancel}
            onPointerEnter={onPointerEnter}
            onKeyDown={onKeyDown}
            onMouseEnter={onMouseEnter}
            onMouseMove={onMouseMove}
            onFocus={onFocus}
            onClick={onClick}
            style={style}
          >
            {vod.isLive && <span className="timeline-live-marker" aria-hidden="true" />}
          </Link>
        )
      })}
      <div className="timeline-now" />
      {previewTime !== null && !activeVod?.subscriberOnly && (touchSelection !== null || hoveredId !== null) && (
        <div
          className="timeline-play-marker"
          style={{ left: `${((previewTime - start) / rangeMs) * 100}%` }}
          aria-hidden="true"
        />
      )}
      {activeVod && (
        <div
          ref={popupRef}
          id={tooltipId}
          role="group"
          aria-label="Recording details"
          className="timeline-tooltip"
          style={{
            left: `clamp(8px, calc(${((tooltipTime - start) / rangeMs) * 100}% - 8px), max(8px, calc(100% - 308px)))`,
          }}
        >
          <strong>{slug} {activeVod.isLive && <span className="timeline-live-label">LIVE</span>}</strong>
          <span>Start: {formatAbsolute(activeVod.startTime)}</span>
          <span>End: {activeVod.isLive ? 'Ongoing' : formatAbsolute(recordingEnd(activeVod, end))}</span>
          <span>{activeVod.isLive ? 'Elapsed' : 'Duration'}: {formatDuration(recordingEnd(activeVod, end) - activeVod.startTime)}</span>
          {!activeVod.subscriberOnly && previewTime !== null && (
            <span>Play at: {formatAbsolute(previewTime)} ({formatTimecode(playbackOffset(activeVod, previewTime, end))})</span>
          )}
          <div className="timeline-watch-actions">
            {activeVod.subscriberOnly ? (
              <span className="timeline-subscriber-note">Subscriber only</span>
            ) : (
              <>
                <Link to={playerUrl(activeVod)} onFocus={() => setFocusedId(activeVod.id)}>Watch from start</Link>
                {touchSelection && previewTime !== null && (
                  <Link to={playerUrl(activeVod, playbackOffset(activeVod, previewTime, end))}>
                    Watch from here
                  </Link>
                )}
                {activeVod.isLive && (
                  <a
                    href={`https://destiny.gg/bigscreen#kick/${encodeURIComponent(slug)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onFocus={() => setFocusedId(activeVod.id)}
                  >
                    Watch live
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export function TimelineView() {
  const { channels } = useAppState()
  const [params, setParams] = useSearchParams()
  const favoritesOnly = params.get('favorites') === '1'
  const visibleChannels = favoritesOnly ? channels.filter((channel) => channel.favorite) : channels
  const channelMeta = useChannelsMeta(visibleChannels.map((channel) => channel.slug))
  const [hoveredChannel, setHoveredChannel] = useState<string | null>(null)
  const [focusedChannel, setFocusedChannel] = useState<string | null>(null)
  const selectedChannel = hoveredChannel ?? focusedChannel
  const highlightedChannel = visibleChannels.some((channel) => channel.slug === selectedChannel) ? selectedChannel : null
  const highlightClass = (slug: string) => highlightedChannel === null ? '' : highlightedChannel === slug ? 'is-highlighted' : 'is-dimmed'
  // Keep the window stable while inspecting the timeline.
  const [initialEnd] = useState(() => Date.now())
  const requestedEnd = Number(params.get('end'))
  const end = Number.isFinite(requestedEnd) && requestedEnd > 0 && requestedEnd <= Date.now() ? requestedEnd : initialEnd
  const range = RANGES.find((option) => option.hours === Number(params.get('range'))) ?? RANGES[0]
  const rangeHours = range.hours
  const rangeMs = rangeHours * HOUR_MS
  const start = end - rangeMs
  const activity = useChatActivity(start, end)
  const [activityInspection, setActivityInspection] = useState<ActivityInspectionState | null>(null)
  const inspectActivity = useCallback((timestampMs: number, source: ActivityInspectionSource) => {
    setActivityInspection({ timestampMs, source })
  }, [])
  const dismissActivityInspection = useCallback(() => setActivityInspection(null), [])
  useEffect(() => { setActivityInspection(null) }, [start, end])
  const chartRef = useRef<HTMLDivElement>(null)
  const [chartWidth, setChartWidth] = useState(800)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshErrors, setRefreshErrors] = useState<string[]>([])
  const [refreshVersions, setRefreshVersions] = useState<Record<string, number>>({})
  const [channelStatuses, setChannelStatuses] = useState<Record<string, ChannelStatus>>({})
  const updateChannelStatus = useCallback((slug: string, status: ChannelStatus) => {
    setChannelStatuses((previous) => {
      const current = previous[slug]
      if (current?.loading === status.loading && current.error === status.error && current.empty === status.empty && current.retry === status.retry) return previous
      return { ...previous, [slug]: status }
    })
  }, [])
  const refreshController = useRef<AbortController | null>(null)

  useEffect(() => () => refreshController.current?.abort(), [])

  const refresh = async () => {
    if (refreshController.current) return
    const controller = new AbortController()
    refreshController.current = controller
    setRefreshing(true)
    setRefreshErrors([])
    const next = new URLSearchParams(params)
    const nextEnd = Date.now()
    if (nextEnd === end) activity.reload()
    next.set('end', String(nextEnd))
    setParams(next, { replace: true })

    const results = await Promise.allSettled(visibleChannels.map(async ({ slug }) => {
      await refreshVods(slug, controller.signal)
      return slug
    }))
    if (controller.signal.aborted) return
    setRefreshVersions((previous) => {
      const versions = { ...previous }
      for (const result of results) {
        if (result.status === 'fulfilled') versions[result.value] = (versions[result.value] ?? 0) + 1
      }
      return versions
    })
    setRefreshErrors(results.flatMap((result, index) => result.status === 'rejected' ? [visibleChannels[index].slug] : []))
    refreshController.current = null
    setRefreshing(false)
  }

  useEffect(() => {
    if (params.get('range') === String(rangeHours) && params.get('end') === String(end)) return
    const next = new URLSearchParams(params)
    next.set('range', String(rangeHours))
    next.set('end', String(end))
    setParams(next, { replace: true })
  }, [params, setParams, rangeHours, end])
  const days: Array<{ start: number; end: number; midnight: number }> = []
  const day = new Date(start)
  day.setHours(0, 0, 0, 0)
  while (day.getTime() < end) {
    const midnight = day.getTime()
    // Advance calendar dates rather than adding 24h: DST days can be 23h or 25h.
    day.setDate(day.getDate() + 1)
    days.push({ start: Math.max(start, midnight), end: Math.min(end, day.getTime()), midnight })
  }

  const ticks: number[] = []
  const desiredStep = rangeHours / Math.max(1, Math.floor(chartWidth / 80))
  const tickHours = [2, 3, 4, 6, 12, 24].find((hours) => hours >= desiredStep) ?? 24
  for (const { midnight } of days) {
    for (let hour = 0; hour < 24; hour += tickHours) {
      const tick = new Date(midnight)
      tick.setHours(hour, 0, 0, 0)
      const time = tick.getTime()
      if (time < start || time >= end) continue
      // Leave room for Now and avoid crowding across calendar/DST boundaries.
      if ((end - time) / rangeMs * chartWidth < 65) continue
      const previous = ticks.at(-1)
      if (previous !== undefined && (time - previous) / rangeMs * chartWidth < 65) continue
      ticks.push(time)
    }
  }

  useLayoutEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const observer = new ResizeObserver(([entry]) => setChartWidth(entry.contentRect.width))
    observer.observe(chart)
    return () => observer.disconnect()
  }, [])

  return (
    <section className="timeline-page" aria-labelledby="timeline-title">
      <div className="timeline-heading">
        <div>
          <h1 id="timeline-title">Explore endless content loop</h1>
          <p>{range.description} · Local time</p>
          <p className="timeline-window-label">{dateFormat.format(start)} – {dateFormat.format(end)}</p>
        </div>
        <div className="timeline-toolbar">
          <div className="timeline-filters" role="group" aria-label="Timeline channels">
            {[{ label: 'All channels', favorites: false }, { label: 'Favorites', favorites: true }].map((option) => (
              <button
                key={option.label}
                className={`btn ${favoritesOnly === option.favorites ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={favoritesOnly === option.favorites}
                onClick={() => {
                  const next = new URLSearchParams(params)
                  if (option.favorites) next.set('favorites', '1')
                  else next.delete('favorites')
                  setParams(next, { replace: true })
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="timeline-ranges" role="group" aria-label="Timeline range">
            {RANGES.map((option) => (
              <button
                key={option.hours}
                className={`btn ${rangeHours === option.hours ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={rangeHours === option.hours}
                onClick={() => {
                  const next = new URLSearchParams(params)
                  next.set('range', String(option.hours))
                  next.set('end', String(Date.now()))
                  setParams(next, { replace: true })
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button className="btn" onClick={refresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>
      {refreshErrors.length > 0 && (
        <p className="timeline-refresh-error" role="status">
          Could not refresh: {refreshErrors.join(', ')}. Previously loaded VODs have been kept. Try Refresh again.
        </p>
      )}
      <div
        ref={chartRef}
        className="timeline-chart"
        role="region"
        aria-label={`${range.description} timeline`}
      >
        <div className="timeline-content">
          <div className="timeline-dates" aria-label="Calendar days">
            {days.map((day) => (
              <div
                key={day.midnight}
                className="timeline-date"
                title={dateFormat.format(day.midnight)}
                aria-label={dateFormat.format(day.midnight)}
                style={{
                  left: `${((day.start - start) / rangeMs) * 100}%`,
                  width: `${((day.end - day.start) / rangeMs) * 100}%`,
                }}
              >
                <span aria-hidden="true">
                  {(day.end - day.start) / rangeMs * chartWidth >= 56
                    ? dateFormat.format(day.midnight)
                    : (day.end - day.start) / rangeMs * chartWidth >= 24
                      ? dayFormat.format(day.midnight)
                      : null}
                </span>
              </div>
            ))}
          </div>
          <div className="timeline-canvas">
            {ticks.map((time) => (
              <div
                key={time}
                className="timeline-tick"
                style={{ left: `${((time - start) / rangeMs) * 100}%` }}
              >
                <span className="timeline-tick-label">
                  <strong>{timeFormat.format(time)}</strong>
                </span>
              </div>
            ))}
            <div className="timeline-now"><span>Now</span></div>
          </div>
          <div className="timeline-inspect-region">
            <ChatActivityGraph
              data={activity.data}
              from={start}
              to={end}
              width={chartWidth}
              ticks={ticks}
              loading={activity.loading}
              inspection={activityInspection}
              onInspect={inspectActivity}
              onDismiss={dismissActivityInspection}
            />
            <div className="timeline-plot">
              {visibleChannels.map((channel) => (
                <ChannelRow
                  key={`${channel.slug}:${refreshVersions[channel.slug] ?? 0}`}
                  slug={channel.slug}
                  start={start}
                  end={end}
                  ticks={ticks}
                  chartWidth={chartWidth}
                  highlightClass={highlightClass(channel.slug)}
                  onStatus={updateChannelStatus}
                />
              ))}
            </div>
            {activityInspection && (
              <div
                className="timeline-inspection-guide"
                style={{ left: `${Math.max(0, Math.min(100, ((activityInspection.timestampMs - start) / rangeMs) * 100))}%` }}
                aria-hidden="true"
              />
            )}
          </div>
        </div>
      </div>
      <ul className="timeline-legend" aria-label="Channel colors">
        {visibleChannels.map(({ slug }) => (
          <li
            key={slug}
            tabIndex={0}
            className={highlightClass(slug)}
            aria-label={`Highlight recordings for ${slug}`}
            onMouseEnter={() => setHoveredChannel(slug)}
            onMouseLeave={() => setHoveredChannel(null)}
            onFocus={() => setFocusedChannel(slug)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setFocusedChannel(null)
            }}
          >
            <span className="timeline-legend-swatch" style={{ backgroundColor: channelColor(slug) }} aria-hidden="true" />
            <Avatar src={channelMeta.get(slug)?.profilePic} name={slug} size={24} />
            <span className="timeline-legend-details">
              <span>{slug}</span>
              <span className="timeline-legend-status" role="status" aria-label={`${slug} VOD status`}>
                {channelStatuses[slug]?.error ? (
                  <span title={channelStatuses[slug].error ?? undefined}>Could not load VODs</span>
                ) : !channelStatuses[slug] || channelStatuses[slug].loading ? (
                  'Loading VODs…'
                ) : channelStatuses[slug].empty ? (
                  'No VODs in this range'
                ) : null}
              </span>
            </span>
            {channelStatuses[slug]?.error && (
              <button
                className="btn timeline-legend-retry"
                onClick={channelStatuses[slug].retry}
                aria-label={`Retry loading VODs for ${slug}`}
              >
                Retry
              </button>
            )}
          </li>
        ))}
      </ul>
      <ChatActivityStatus activity={activity} />
    </section>
  )
}
